import React, { createContext, useContext, useEffect } from 'react'
import { Redirect, useParams } from 'react-router-dom'

import { ApolloError, ApolloQueryResult } from 'apollo-boost'

import { useRefetchOnFocus } from '@app/common/helpers/useRefetchOnFocus'
import {
  AlertType,
  useNotifications
} from '@app/common/services/Notifications/NotificationsProvider'
import { CourtCaseState, Exact } from '@app/graphql/types'
import { useIntl } from '@app/intl/Intl'
import { getCourtCasesBasePath } from '@app/modules/CourtCases'
import { CourtCaseQuery, useCourtCaseQuery } from '@app/queries/CourtCases/CourtCase.queries'

import { useCourtCaseTransition } from './hooks/useTransitionCourtCase'

export interface CourtCaseContextValues {
  loading: boolean
  error: ApolloError | undefined
  data: CourtCaseQuery['courtCases']['items'][number] | undefined
  refetch: (variables?: Exact<{ uuid: string }>) => Promise<ApolloQueryResult<CourtCaseQuery>>
}

const CourtCaseContext = createContext<CourtCaseContextValues>(
  null as unknown as CourtCaseContextValues
)

export const CourtCaseProvider = ({ children }) => {
  const { id, courtCaseUuid } = useParams<{ id: string; courtCaseUuid: string }>()
  const { pushNotification } = useNotifications()
  const { formatMessage } = useIntl()
  const customerId = Number(id)
  const { transitionToOpenedByLawyer } = useCourtCaseTransition()

  const { loading, error, data, refetch } = useCourtCaseQuery({
    variables: {
      customerId,
      uuid: courtCaseUuid
    },
    fetchPolicy: 'no-cache'
  })

  useRefetchOnFocus(refetch)

  const courtCaseData = data?.courtCases.items[0]

  useEffect(() => {
    if (courtCaseData?.state === CourtCaseState.ClientFileApproved) {
      transitionToOpenedByLawyer(courtCaseData.uuid)
    }
  }, [courtCaseData?.state])

  if (!loading && !courtCaseData) {
    pushNotification({
      type: AlertType.error,
      message: formatMessage({
        id: 'CourtCases.Error.CaseDoesNotBelongToCustomer'
      })
    })

    return <Redirect to={getCourtCasesBasePath(customerId)} />
  }

  return (
    <CourtCaseContext.Provider
      value={{
        loading,
        error,
        data: courtCaseData,
        refetch
      }}
    >
      {children}
    </CourtCaseContext.Provider>
  )
}

export const useCourtCaseContext = (): CourtCaseContextValues => {
  return useContext(CourtCaseContext)
}
